import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Badge from '../components/badge/Badge';
import Input from '../components/input/Input';
import DropDown, { ItemProp } from '../components/dropdown/DropDown';
import ShareFormModal from '../components/modals/ShareFormModal';
import ShareDisableConfirmModal from '../components/modals/ShareDisableConfirmModal';
import { useShares } from '../hooks/useShares';
import { Share } from '../types/interfaces';
import { routes } from '../routes/routePaths';

const SEARCH_DEBOUNCE_MS = 300;

const formatIsoDateToDdMmYyyy = (isoDate: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? '');
  if (!match) return isoDate ?? '';
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

const formatNominalValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const formatIssuedQuantity = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('es-AR');
};

const shareTypeFilterItems = (shareTypes: { id: number; name: string }[]): ItemProp[] => [
  { id: '', label: 'Todos' },
  ...shareTypes.map((shareType) => ({ id: String(shareType.id), label: shareType.name })),
];

const statusFilterItems: ItemProp[] = [
  { id: '', label: 'Todos' },
  { id: 'true', label: 'Activo' },
  { id: 'false', label: 'Inactivo' },
];

const SharesConfiguration = () => {
  const navigate = useNavigate();
  const {
    shares,
    isLoading,
    isSaving,
    isToggling,
    filters,
    setFilters,
    createShare,
    updateShare,
    toggleEnabled,
    formMetadata,
  } = useShares();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingShare, setEditingShare] = useState<Share | null>(null);
  const [shareToDisable, setShareToDisable] = useState<Share | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState(filters.search ?? '');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm || undefined }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleGoBack = () => {
    navigate(routes.settings);
  };

  const handleOpenCreateModal = () => {
    setEditingShare(null);
    setIsModalOpen(true);
  };

  const handleEditShare = useCallback((share: Share) => {
    setEditingShare(share);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingShare(null);
  };

  const handleShareTypeFilterChange = (_title: string, item?: ItemProp) => {
    const shareTypeId = item?.id ? Number(item.id) : null;
    setFilters({ ...filters, shareTypeId });
  };

  const handleStatusFilterChange = (_title: string, item?: ItemProp) => {
    const enabled = item?.id === '' || item?.id === undefined ? null : item.id === 'true';
    setFilters({ ...filters, enabled });
  };

  const handleDisableClick = useCallback((share: Share) => {
    setShareToDisable(share);
  }, []);

  const handleCancelDisable = () => {
    setShareToDisable(null);
  };

  const handleConfirmDisable = async () => {
    if (!shareToDisable) return;
    const id = shareToDisable.id;
    setTogglingId(id);
    try {
      const result = await toggleEnabled(id, false);
      if (result.success) {
        toast.success('Acción dada de baja');
      } else {
        toast.error(result.message || 'Error al cambiar el estado');
      }
    } catch {
      toast.error('Error al cambiar el estado');
    } finally {
      setTogglingId(null);
      setShareToDisable(null);
    }
  };

  const handleReactivate = useCallback(
    async (share: Share) => {
      setTogglingId(share.id);
      try {
        const result = await toggleEnabled(share.id, true);
        if (result.success) {
          toast.success('Acción reactivada');
        } else {
          toast.error(result.message || 'Error al cambiar el estado');
        }
      } catch {
        toast.error('Error al cambiar el estado');
      } finally {
        setTogglingId(null);
      }
    },
    [toggleEnabled],
  );

  const columns = useMemo(
    () => [
      {
        name: 'Nombre',
        selector: (row: Share) => row.name,
        cell: (row: Share) => <Card>{row.name}</Card>,
        sortable: true,
      },
      {
        name: 'Ticker',
        selector: (row: Share) => row.ticker,
        cell: (row: Share) => <Card>{row.ticker}</Card>,
        sortable: true,
        width: '110px',
      },
      {
        name: 'Tipo de acción',
        selector: (row: Share) => row.shareTypeName,
        cell: (row: Share) => <Card>{row.shareTypeName}</Card>,
        sortable: true,
      },
      {
        name: 'Moneda',
        selector: (row: Share) => row.currencyDescription,
        cell: (row: Share) => <Card>{row.currencyDescription}</Card>,
        sortable: true,
      },
      {
        name: 'Valor nominal',
        selector: (row: Share) => row.nominalValue,
        cell: (row: Share) => <Card>{formatNominalValue(row.nominalValue)}</Card>,
        sortable: true,
      },
      {
        name: 'Cantidad emitida',
        selector: (row: Share) => row.issuedQuantity,
        cell: (row: Share) => <Card>{formatIssuedQuantity(row.issuedQuantity)}</Card>,
        sortable: true,
      },
      {
        name: 'Fecha de emisión',
        selector: (row: Share) => row.issueDate,
        cell: (row: Share) => <Card>{formatIsoDateToDdMmYyyy(row.issueDate)}</Card>,
        sortable: true,
      },
      {
        name: 'Estado',
        selector: (row: Share) => (row.enabled ? 'Activo' : 'Inactivo'),
        cell: (row: Share) => (
          <Badge
            type='text'
            size='sm'
            weight='bold'
            variant={row.enabled ? 'success' : 'neutral-strong'}
          >
            {row.enabled ? 'Activo' : 'Inactivo'}
          </Badge>
        ),
        sortable: true,
        width: '120px',
      },
      {
        name: 'Acciones',
        cell: (row: Share) => (
          <Card display='flex' gap='8px' alignItems='center'>
            <Icon
              name='edit_outline'
              width={20}
              height={20}
              cursor='pointer'
              variant='info'
              onClick={() => handleEditShare(row)}
              aria-label={`Editar acción ${row.name}`}
            />
            <Icon
              name={row.enabled ? 'cancel_outline' : 'refresh_outline'}
              width={20}
              height={20}
              cursor='pointer'
              variant={row.enabled ? 'warning' : 'success'}
              disabled={togglingId === row.id || isToggling}
              onClick={() => (row.enabled ? handleDisableClick(row) : handleReactivate(row))}
              aria-label={
                row.enabled ? `Dar de baja acción ${row.name}` : `Reactivar acción ${row.name}`
              }
            />
          </Card>
        ),
        width: '120px',
      },
    ],
    [togglingId, isToggling, handleEditShare, handleDisableClick, handleReactivate],
  );

  const shareTypeItems = shareTypeFilterItems(formMetadata?.shareTypes ?? []);
  const selectedShareTypeTitle =
    shareTypeItems.find((item) => Number(item.id) === filters.shareTypeId)?.label ?? 'Todos';
  const selectedStatusTitle =
    filters.enabled === null || filters.enabled === undefined
      ? 'Todos'
      : filters.enabled
        ? 'Activo'
        : 'Inactivo';

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing='4'
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <Card display='flex' alignItems='center' gap='8px'>
          <Button
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label='Volver a configuración'
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Acciones
          </H>
        </Card>

        <Button variant='primary' onClick={handleOpenCreateModal}>
          + Nueva acción
        </Button>
      </Card>

      <Card display='flex' gap='16px' alignItems='flex-end' margin={{ bottom: '3' }} flexWrap='wrap'>
        <div style={{ flex: '0 0 280px', minWidth: 0 }}>
          <Input
            id='share-search-filter'
            label='Buscar'
            name='search'
            type='text'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size='md'
            placeholder='Buscar por nombre o ticker'
          />
        </div>
        <div style={{ flex: '0 0 280px', minWidth: 0 }}>
          <DropDown
            id='share-type-filter'
            label='Tipo de acción'
            title={selectedShareTypeTitle}
            items={shareTypeItems}
            onChange={handleShareTypeFilterChange}
            size='md'
          />
        </div>
        <div style={{ flex: '0 0 280px', minWidth: 0 }}>
          <DropDown
            id='share-status-filter'
            label='Estado'
            title={selectedStatusTitle}
            items={statusFilterItems}
            onChange={handleStatusFilterChange}
            size='md'
          />
        </div>
      </Card>

      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : shares.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={shares}
            tableData={{
              size: shares.length,
              page: 0,
              totalElements: shares.length,
              totalPages: 1,
            }}
            handleChangePage={() => {}}
          />
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Icon name='folder_empty' width={80} height={80} variant='primary' />
          </Card>
          <H variant='primary' align='center' withMargin={false} weight='bold' type='3'>
            No hay acciones registradas.
          </H>
        </Card>
      )}

      <ShareFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleCloseModal}
        shareToEdit={editingShare}
        formMetadata={formMetadata}
        createShare={createShare}
        updateShare={updateShare}
        isSaving={isSaving}
      />

      <ShareDisableConfirmModal
        isOpen={Boolean(shareToDisable)}
        share={shareToDisable}
        onConfirm={handleConfirmDisable}
        onCancel={handleCancelDisable}
        isLoading={isToggling}
      />
    </Card>
  );
};

export default SharesConfiguration;
